import { CaretDownIcon } from "@phosphor-icons/react";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslations } from "use-intl";

import { Drawer } from "@/components/Drawer/Drawer";
import { DrawerTopBar } from "@/components/Drawer/DrawerTopBar";
import { FilterButton } from "@/components/FilterButton/FilterButton";
import { CategorySwitcher } from "@/features/bookings/components/CategorySwitcher/CategorySwitcher";
import { reservationTypesLabels } from "@/features/bookings/components/labels";
import { MyFlightsList } from "@/features/bookings/components/MyFlightsList";
import { MyHotelsList } from "@/features/bookings/components/MyHotelsList";
import { OrdersTypeList } from "@/features/bookings/components/OrdersTypeList";
import type { ReservationType } from "@/features/bookings/types/types";
import { MyTripsList } from "@/features/trips/components/bookingDetails/MyTripsList";
import {
  rudderAnalytics,
  FlightTrackingEvents,
  HotelTrackingEvents,
  TripTrackingEvents,
} from "@/lib/rudderstack";
import type { SearchScope } from "@/types/types";

interface Props {
  category: SearchScope;
  initReservationType: ReservationType;
}

export const MyBookings = ({ category, initReservationType }: Props) => {
  const navigate = useNavigate();
  const t = useTranslations();
  const [selectedReservationType, setSelectedReservationType] =
    useState<ReservationType>(initReservationType);
  const [reservationTypeDrawerOpen, setReservationTypeDrawerOpen] =
    useState(false);

  const reservationTypeSwitcher = (
    <FilterButton onClick={() => setReservationTypeDrawerOpen(true)}>
      {t(reservationTypesLabels[selectedReservationType])}
      <CaretDownIcon aria-hidden className="shrink-0" weight="bold" />
    </FilterButton>
  );

  return (
    <>
      <div className="mb-6 flex justify-center">
        <CategorySwitcher current={category} />
      </div>

      <>
        {category === "flights" ? (
          <MyFlightsList
            onReservationClick={(reservationId) => {
              rudderAnalytics.track(
                FlightTrackingEvents.USER_ENTERED_PARTICULAR_ORDER,
              );
              navigate({
                to: "/bookings/$scope/$id",
                params: { scope: "flights", id: reservationId },
              });
            }}
            reservationType={selectedReservationType}
            reservationTypeSwitcher={reservationTypeSwitcher}
          />
        ) : category === "hotels" ? (
          <MyHotelsList
            onReservationClick={(reservationId) => {
              rudderAnalytics.track(
                HotelTrackingEvents.USER_ENTERED_PARTICULAR_ORDER,
              );
              navigate({
                to: "/bookings/$scope/$id",
                params: { scope: "hotels", id: reservationId },
              });
            }}
            reservationType={selectedReservationType}
            reservationTypeSwitcher={reservationTypeSwitcher}
          />
        ) : category === "trips" ? (
          <MyTripsList
            onReservationClick={(reservationId) => {
              rudderAnalytics.track(
                TripTrackingEvents.USER_ENTERED_PARTICULAR_ORDER,
              );
              navigate({
                to: "/bookings/$scope/$id",
                params: { scope: "trips", id: reservationId },
              });
            }}
          />
        ) : null}
      </>

      <Drawer
        open={reservationTypeDrawerOpen}
        onClose={() => setReservationTypeDrawerOpen(false)}
      >
        <DrawerTopBar
          onCloseClick={() => setReservationTypeDrawerOpen(false)}
          title={t("reservations.whichTripsToShow")}
        />

        <OrdersTypeList
          value={selectedReservationType}
          onValueChange={(newValue) => {
            setSelectedReservationType(newValue);
            setReservationTypeDrawerOpen(false);
          }}
        />
      </Drawer>
    </>
  );
};
